import React, { FC, useCallback, useEffect, useReducer, useState } from 'react';
import { Collapse, Typography, useTheme } from '@material-ui/core';
import {
  IStudentAssignment,
  IStudentPaginatedAssignments,
} from '../../../../config/api/models/Assignment';
import StudentAssignmentItem from './StudentAssignmentItem';
import { useTranslation } from 'react-i18next';
import { AssignmentSections } from './StudentAssignments';
import { useFetch, useUser } from '../../../shared/hooks';
import apiRoutes from '../../../../config/api/routes';
import { GetMoreAssignmentsButton } from '../../../shared/components';
import {
  AssignmentsWrapper,
  ContentWrapper,
  Spacer,
  StyledLocalLoading,
  TitleWrapper,
} from './Atoms';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';

type Props = {
  section: AssignmentSections;
  open: boolean;
  onToggle: (section: AssignmentSections) => void;
  bookFilter?: string;
};

export interface IPageArgs {
  userId: string;
  page: number;
  bookFilter?: string | undefined;
  groupFilter?: string | undefined;
}

const StudentAssignmentsSection: FC<Props> = ({ section, open, onToggle, bookFilter }) => {
  const user = useUser();
  const { t } = useTranslation();
  const [lastPageLoaded, setLastPageLoaded] = useState(false);
  const [pageArgs, setPageArgs] = useState<IPageArgs>({
    userId: user.user.id,
    bookFilter,
    page: -1, //just initializing the value as a negative page to prevent calling the API twice
  });
  const theme = useTheme();

  const [assignments, patchAssignments] = useReducer(
    (state: any, action: any) => {
      switch (action.type) {
        case 'add':
          return { list: action.payload };
        case 'reset':
          return { list: [] };
      }
      return { list: [] };
    },
    { list: [] }
  );

  const [assignmentsResponse, getAssignments] = useFetch<IStudentPaginatedAssignments>('');

  const onToogleAndReset = useCallback(() => {
    onToggle(section);
    setPageArgs({ ...pageArgs, page: 1 });
  }, [pageArgs, onToggle, section]);

  const fetchAssignments = useCallback(
    (pageArgs: IPageArgs, section: string) => {
      if (pageArgs.page === 1) patchAssignments({ type: 'reset' });

      getAssignments({
        path:
          section === 'todo'
            ? apiRoutes.openStudentAssignments(pageArgs)
            : apiRoutes.closedStudentAssignments(pageArgs),
      });
    },
    [getAssignments]
  );

  useEffect(() => {
    if (assignmentsResponse.success) {
      setLastPageLoaded(
        assignmentsResponse.data.pagination.currentPage ===
          assignmentsResponse.data.pagination.totalPages
      );
      patchAssignments({
        type: 'add',
        payload: assignmentsResponse.data.data,
      });
    }
  }, [assignmentsResponse, patchAssignments]);

  useEffect(() => {
    if (open && pageArgs.page > 0) fetchAssignments(pageArgs, section);
  }, [pageArgs, fetchAssignments, section, open]);

  useEffect(() => {
    setPageArgs((currentValue) => ({ ...currentValue, page: 1, bookFilter }));
  }, [bookFilter]);

  return (
    <ContentWrapper>
      <TitleWrapper onClick={onToogleAndReset}>
        <Typography variant="h3">
          {t(
            `studentDashboard.assignments.${
              section === 'todo' ? 'assignmentsTodo' : 'assignmentsDone'
            }`
          )}
        </Typography>
        <Spacer />
        <FontAwesomeSpriteIcon
          icon={open ? SpriteIconsLight.ANGLE_UP : SpriteIconsLight.ANGLE_DOWN}
          color={theme.palette.secondary.main}
          iconSize="md"
        />
        {assignments.list.length === 0 && assignmentsResponse.pending && <StyledLocalLoading />}
      </TitleWrapper>
      <Collapse component={AssignmentsWrapper} in={open}>
        {assignments.list.length === 0 ? (
          <p style={{ textAlign: 'center' }}>{t('shared.noResults')}</p>
        ) : assignments.list.length > 0 ? (
          <>
            {assignments.list.map((assignment: IStudentAssignment) => (
              <StudentAssignmentItem
                key={assignment.id}
                assignment={assignment}
                onFinishAssignment={() => setPageArgs({ ...pageArgs, page: 1 })}
              />
            ))}
            {!lastPageLoaded && (
              <GetMoreAssignmentsButton
                label={t('studentDashboard.assignments.loadMore')}
                loading={assignmentsResponse.pending}
                onClick={() => setPageArgs({ ...pageArgs, page: pageArgs.page + 1 })}
              />
            )}
          </>
        ) : null}
      </Collapse>
    </ContentWrapper>
  );
};

export default StudentAssignmentsSection;
